import { createMuiTheme } from '@material-ui/core/styles'
import { heading } from '../../utils/utils'

const mainFont = 'Poppins'

// Atlantic - Brand Color
const rougeAtlantic = '#EE3124'

// Atlantic - Couleurs principales (Primary Colors)
const saumon = '#F26F5F'
const saumonDark = '#E55A4A' // darker salmon for hover
const contour = '#F2C8C1'
const blanc = '#FFFFFF'
const filet = '#E6E6E6'
const texte = '#2F2F2F'
const texteSecondaire = '#716F6F'
const backgroundGris = '#F5F5F5'
const backgroundDashboard = '#E4EDF7'
const background = '#FFF2E6'
const backgroundTrame = 'rgba(255, 242, 230, 0.7)'

// Atlantic - Couleurs CTA (Call to Action)
const ctaDefaut = '#4F8BD1'
const ctaHover = '#3B76BA'

// Atlantic - Couleurs statuts (Status Colors)
const alerteErreur = '#D93E25'
const warning = '#F6A243'
const validation = '#85898E'

// Atlantic - Couleurs system (System Colors)
const rougeSystem = '#C02327'
const grisSystem = '#706F6F'
const bleuSystem = '#5ECCD9'
const backgroundSystem = '#EEFAFB'

// Aliases for theme compatibility (primary = CTA for buttons, accent = brand)
const white = blanc
const black = texte
const primaryMain = ctaDefaut
const primaryDark = ctaHover
const primaryLight = contour
const secondaryMain = saumon

const f14 = `14px`
const f16 = `16px`
const f18 = `18px`
const f20 = `20px`
const f22 = `22px`
const f30 = `30px`
const f36 = `36px`

const body = {
  fontSize: f18,
  lineHeight: `27px`,
  fontWeight: 400,
}
// const body2 = {
//   fontSize: f14,
//   lineHeight: 1.67,
// }

const primaryButtonIntent = {
  backgroundColor: `${ctaDefaut} !important`,
  border: `1px solid ${ctaDefaut} !important`,
  color: `${white} !important`,
  height: '58px !important',
  boxShadow: 'none !important',
  '& .MuiSvgIcon-root': {
    color: 'inherit',
  },
  '&:hover, &:focus, &:active, &.selected': {
    backgroundColor: `${ctaHover} !important`,
    borderColor: `${ctaHover} !important`,
    boxShadow: 'none !important',
  },
  '&.Mui-disabled': {
    backgroundColor: `${filet} !important`,
    borderColor: `${filet} !important`,
    color: `${texteSecondaire} !important`,
  },
}

const secondaryButtonIntent = {
  backgroundColor: `${white} !important`,
  border: `1px solid ${ctaDefaut} !important`,
  color: `${ctaDefaut} !important`,
  boxShadow: 'none !important',
  '& .MuiSvgIcon-root': {
    color: 'inherit',
  },
  '&:hover, &:focus, &:active, &.selected': {
    backgroundColor: `${ctaHover} !important`,
    borderColor: `${ctaHover} !important`,
    color: `${white} !important`,
    boxShadow: 'none !important',
  },
  '&.Mui-disabled': {
    backgroundColor: `${white} !important`,
    borderColor: `${filet} !important`,
    color: `${texteSecondaire} !important`,
  },
}

const tertiaryButtonIntent = {
  backgroundColor: 'transparent !important',
  border: 'none !important',
  color: `${ctaDefaut} !important`,
  boxShadow: 'none !important',
  '& .MuiSvgIcon-root': {
    color: 'inherit',
  },
  '&:hover, &:focus, &:active, &.selected': {
    backgroundColor: `${backgroundSystem} !important`,
    color: `${ctaHover} !important`,
    boxShadow: 'none !important',
  },
  '&.Mui-disabled': {
    color: `${texteSecondaire} !important`,
  },
}

const tertiarySelectIntent = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'transparent !important',
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none !important',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none !important',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none !important',
    },
  },
  '& .MuiSelect-select, & .MuiSelect-icon, & .MuiInputBase-input': {
    color: `${ctaDefaut} !important`,
  },
  '& .MuiSelect-select:focus': {
    backgroundColor: 'transparent !important',
  },
}

const content = {
  fontSize: `${f18} !important`,
  lineHeight: `27px !important`,
}

/** Full Atlantic palette - available for components needing specific colors */
export const atlanticPalette = {
  rougeAtlantic,
  saumon,
  saumonDark,
  contour,
  blanc,
  filet,
  texte,
  texteSecondaire,
  backgroundGris,
  backgroundDashboard,
  background,
  backgroundTrame,
  ctaDefaut,
  ctaHover,
  alerteErreur,
  warning,
  validation,
  rougeSystem,
  grisSystem,
  bleuSystem,
  backgroundSystem,
}

const breakpoints = {
  xs: 0,
  sm: 480,
  md: 768,
  lg: 1024,
  l: 1200,
  xl: 1440,
}

const theme = createMuiTheme({
  themeFont: {
    //WARNING FONT NAME WITH SPACE SHOULD HAVE A +. NEXT JS TRIGGER BAD URL FOR GOOGLE FONT
    main: 'Poppins',
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 480,
      md: 768,
      lg: 1024,
      xl: 1440,
    },
  },
  typography: {
    fontFamily: `${mainFont}, sans-serif`,
    h1: {
      lineHeight: `44px`,
      ...heading(f36, f36, 400, 400, mainFont, mainFont, false),
    },
    subtitle1: {
      lineHeight: `25px`,
      ...heading(f22, f22, 400, 400, mainFont, mainFont, false),
    },
    h2: {
      lineHeight: `35px`,
      ...heading(f30, f30, 500, 500, mainFont, mainFont, false),
    },
    h3: {
      lineHeight: `30px`,
      ...heading(f22, f22, 600, 600, mainFont, mainFont, false),
    },
    h4: {
      lineHeight: `27px`,
      ...heading(f18, f18, 600, 600, mainFont, mainFont, false),
    },
    h5: {
      lineHeight: `38px`,
      ...heading(f18, f18, 600, 600, mainFont, mainFont, false),
    },
    h6: {
      ...heading(f18, f18, 400, 400, mainFont, mainFont, false),
    },
    subtitle2: {
      lineHeight: `27px`,
      ...heading(f18, f18, 500, 500, mainFont, mainFont, false),
    },
    body1: {
      ...body,
    },
    body2: {
      ...body,
    },
    caption: {
      fontSize: f14,
      lineHeight: `21px`,
      fontWeight: 400,
    },
    overline: {
      fontSize: f16,
      lineHeight: `24px`,
      fontWeight: 400,
      fontStyle: 'italic',
      textTransform: 'none',
    },
    button: {
      fontSize: f16,
      lineHeight: `24px`,
      fontWeight: 400,
      textTransform: 'inherit',
    },
  },
  palette: {
    common: {
      black: black,
      white: white,
    },
    background: {
      default: background,
      paper: blanc,
    },
    primary: {
      main: primaryMain,
      dark: primaryDark,
    },
    secondary: {
      main: secondaryMain,
      dark: saumonDark,
    },
    success: {
      main: validation,
    },
    error: {
      main: alerteErreur,
    },
    warning: {
      main: warning,
    },
    divider: filet,
    primaryTransparent: {
      main: rougeAtlantic,
      light: primaryLight,
    },
    secondaryTransparent: {
      main: rougeAtlantic,
      light: primaryLight,
    },
    ternary: {
      main: bleuSystem,
      light: backgroundSystem,
    },
    accent: {
      main: rougeAtlantic,
    },
  },
  sizes: {
    headerHeight: 60,
    headerHeightTransparent: 90,
    logoWidth: 110,
    logoHeight: 30,
    footerBG: 168,
    basketWidth: 340,
    basketWidthLG: 440,
  },
  extraColors: {
    grey: grisSystem,
    greyLight: backgroundGris,
    greyMedium: texteSecondaire,
    greyText: texteSecondaire,
  },
  shadows: [
    'none',
    '0px 0px 15px rgba(46, 46, 46, 0.1)',
    '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
    '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
    '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
    '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
    '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
    '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
    '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
    '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
    '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
    '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
    '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
    '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
    '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
    '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
    '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
    '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
    '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)',
  ],
  spacing: 8,
  shape: {
    borderRadius: 4,
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        '.header__cta--faq .MuiTypography-srOnly': {
          width: 'auto !important',
          height: 'auto !important',
        },
        '.header': {
          '&__col': {
            width: 'auto !important',
          },
          '&__right': {
            [`@media (min-width: ${breakpoints.lg}px)`]: {
              justifyContent: 'space-between !important',
              width: '45% !important',
            },
            [`@media (min-width: ${breakpoints.l}px)`]: {
              width: '52% !important',
            },
          },
          '&__layout': {
            height: '100px !important',
          },
          '&__center': {
            justifyContent: 'end !important',
          },
        },
        '.menu__submenu': {
          zIndex: 2,
          fontSize: 15,
          minWidth: '240px !important',
          '& ul': {
            paddingLeft: 20,
            '& li:hover': {
              textDecoration: 'none',
            },
          },
        },
        '.product__area .MuiBreadcrumbs-separator': {
          color: 'transparent',
          position: 'relative',
          fontSize: '1em',
          alignItems: 'end',
          top: 0,
          '&::before': {
            content: '"/"',
            color: texte,
            position: 'absolute',
            left: 0,
          },
        },
        '.product__area .MuiBreadcrumbs-li': {
          color: texte,
          '& a': {
            color: 'inherit',
          },
        },
        '.product__area .MuiBreadcrumbs-li:last-child': {
          color: texteSecondaire,
          '& a': {
            color: 'inherit',
          },
        },
        '.search__button__icon': {
          marginLeft: '10px',
          width: '76%',
        },
        '.page': {
          marginTop: '100px !important',
        },
        '.menu': {
          '&__submenu': {
            '& ul': {
              paddingLeft: '10px !important',
            },
          },
        },
        '.section-infos': {
          '& .MuiGrid-item:last-child .section-infos__content': {
            paddingLeft: '24px !important',
          },
          '& .MuiGrid-direction-xs-row-reverse .MuiGrid-item:last-child .section-infos__content':
            {
              paddingLeft: '0 !important',
              paddingRight: '24px !important',
            },
          '&__content': {
            width: '100% !important',
            maxWidth: '100% !important',
            boxSizing: 'border-box',
            fontSize: 18,
            lineHeight: '27px',
            textAlign: 'justify !important',
            '&.left, &.center, &.right': {
              textAlign: 'justify !important',
            },
            '& p, & .paragraph-class, & h2, & h3': {
              textAlign: 'justify !important',
            },
          },
        },
        '.discover-product': {
          '&__content': {
            width: '86% !important',
            textAlign: 'center !important',
          },
          '&__title': {
            fontSize: '22px !important',
            fontWeight: 'semibold !important',
            lineHeight: '33px !important',
            color: `${black} !important`,
          },
        },
        '.section-hiw__title, .section-faq__title, .section-testimonials__title, .section-discover__title':
          {
            color: `${secondaryMain} !important`,
          },
        '.hiw__title, .hiw__desc': {
          color: `${black} !important`,
        },
        '.footer': {
          '&__top': {
            backgroundColor: `${texte}`,
          },
          '&__bottom': {
            backgroundColor: `${white} !important`,
          },
          '&__bottomRow1Col > a': {
            display: 'block !important',
            textAlign: 'left !important',
          },
          '&__logo': {
            maxWidth: '70% !important',
            display: 'block !important',
            marginRight: 'auto !important',
          },
          '&__menu': {
            flex: 'auto !important',
            '& a': {
              '&:hover, &:focus': {
                color: `${ctaHover} !important`,
              },
            },
          },
        },
        '.product': {
          '&__price': {
            padding: '8px!important',
          },
          '&__desc': {
            fontSize: '14px!important',
            '& :not(h1):not(h2):not(h3):not(h4):not(h5):not(h6)': {
              margin: '0!important',
              fontSize: '14px!important',
            },
          },
        },
        '.menu-mobile': {
          '&__content, &__back': {
            backgroundColor: `${white}!important`,
          },
        },
        '.faq': {
          '&__btn': {
            color: `${primaryMain} !important`,
          },
        },
        '#hero__box': {
          '& p': {
            margin: '0!important',
          },
          '& .title': {
            margin: '0!important',
          },
          '& strong': {
            backgroundColor: `${secondaryMain} !important`, //TODO: a supprimer si pas de fond saumon
            color: `${white} !important`,
            padding: '4px 10px',
          },
        },
        '.header__icons .header__cta--faq': {
          color: `${black} !important`,
        },
        '.productCard': {
          '&__rowCta--second-hand': {
            '& > div': {
              display: 'block !important',
              '& > div': {
                display: 'block !important',
              },
            },
          },
          '&__title': {
            fontWeight: '700 !important',
            fontSize: '18px !important',
          },
          '&__price_start': {
            '@media (max-width: 1024px)': {
              fontSize: '16px!important',
            },
          },
          '&__price': {
            fontSize: '18px !important',
            '@media (max-width: 1024px)': {
              fontSize: '16px !important',
            },
            color: `${secondaryMain} !important`,
            '&--ttc': {
              fontWeight: '700 !important',
              fontSize: '18px !important',
              '@media (max-width: 1024px)': {
                fontSize: '16px !important',
              },
            },
            '&--original': {
              fontSize: '14px !important',
              fontWeight: '500 !important',
            },
          },
        },
        '.header__cta--account': {
          '& .MuiButton-label': {
            fontSize: 0,
          },
        },
        '.header__cta--faq': {
          '& .MuiButton-label svg': {
            display: 'none',
          },
        },
        '.shop-menu': {
          '&__right-controls__container': {
            [`@media (max-width: 480px)`]: {
              justifyContent: 'start',
              marginTop: '1rem',
            },
          },
          '&__sort-input.MuiOutlinedInput-root': {
            minWidth: '186px !important',
          },
        },
        '.shop-filters': {
          fontSize: f18,
        },
        '.product-dynamic-tag': {
          display: 'inline-flex',
          alignItems: 'center',
          marginTop: 8,
        },
        '.product-dynamic-tag__chip': {
          height: 28,
          borderRadius: '9999px !important',
          backgroundColor: '#F8EFD8 !important',
          color: '#C99B2A !important',
          fontWeight: 600,
          fontSize: 14,
          lineHeight: '20px',
          border: '1px solid #F1E3BE',
        },
        '.product-dynamic-tag__chip .MuiChip-label': {
          paddingLeft: 10,
          paddingRight: 12,
        },
        '.product-dynamic-tag__chip .MuiChip-avatar': {
          width: 16,
          height: 16,
          marginLeft: 10,
          marginRight: -2,
          backgroundColor: 'transparent',
        },
        '.product-dynamic-tag__avatar': {
          width: '16px !important',
          height: '16px !important',
        },
        '.product-dynamic-tag__chip .MuiChip-avatar img': {
          filter:
            'brightness(0) saturate(100%) invert(70%) sepia(45%) saturate(798%) hue-rotate(357deg) brightness(92%) contrast(88%)',
        },
        '.product-dynamic-alert': {
          marginTop: 24,
        },
        '.product-dynamic-alert__content': {
          borderRadius: 10,
          border: '1px solid #E8D8B3',
          backgroundColor: '#F7F0E1',
          color: '#5F5850',
          alignItems: 'flex-start',
          boxShadow: 'none',
          padding: 16,
        },
        '.product-dynamic-alert__content.MuiAlert-standardSuccess, .product-dynamic-alert__content.MuiAlert-standardInfo, .product-dynamic-alert__content.MuiAlert-standardWarning, .product-dynamic-alert__content.MuiAlert-standardError':
          {
            backgroundColor: '#F7F0E1',
            color: '#5F5850',
          },
        '.product-dynamic-alert__content .MuiAlert-icon': {
          color: '#C99B2A',
          marginRight: 12,
          padding: 0,
          opacity: 1,
          marginTop: 2,
        },
        '.product-dynamic-alert__content .MuiAvatar-root': {
          width: 18,
          height: 18,
          backgroundColor: 'transparent',
        },
        '.product-dynamic-alert__content .MuiAvatar-root img': {
          filter:
            'brightness(0) saturate(100%) invert(70%) sepia(45%) saturate(798%) hue-rotate(357deg) brightness(92%) contrast(88%)',
        },
        '.product-dynamic-alert__content .MuiAlert-message': {
          padding: 0,
          fontSize: 14,
          lineHeight: '22px',
          color: '#5F5850',
        },
        '.product-dynamic-alert__content .MuiAlert-message p': {
          margin: 0,
        },
        '.product-dynamic-alert__content .MuiAlert-message p + p': {
          marginTop: 4,
        },
        '.product-dynamic-alert__content .MuiAlert-message strong': {
          display: 'block',
          fontWeight: 700,
          fontSize: 16,
          lineHeight: '24px',
          color: '#2F2F2F',
          marginBottom: 4,
        },
        '.product-accordion__summary .MuiIconButton-root': {
          backgroundColor: 'transparent !important',
        },
        '.page--home': {
          '& .ctas > a:first-child .MuiButton-root': {
            ...primaryButtonIntent,
          },
          '& .ctas > a:not(:first-child) .MuiButton-root': {
            ...secondaryButtonIntent,
          },
          '& .section-heros__cta': {
            ...secondaryButtonIntent,
          },
          '& .section-savings__cta, & .section-univers__cta': {
            ...secondaryButtonIntent,
          },
          '& .section-hiw__cta, & .section-faq__cta': {
            ...secondaryButtonIntent,
          },
          '& .section-infos__cta, & .instaWall__instaButton': {
            ...secondaryButtonIntent,
          },
          '& .section-faq__cta': {
            height: '58px !important',
          },
          '& .faq__question .MuiButton-label': {
            ...content,
          },
        },
        '.tagline': {
          ...content,
        },
        '.page--faq': {
          '& .faq__question[aria-expanded="true"]:hover .faq__btn': {
            color: `${white} !important`,
            border: 'none',
          },
        },

        '.page--product': {
          '& .product__btn-addtocart, & .sizes-add__btn': {
            ...primaryButtonIntent,
          },
          '& .product__reserve-btn': {
            ...secondaryButtonIntent,
          },
        },
        '.contact-page__submit, .shipping__next-button, .recapOrder__account_cta':
          {
            ...primaryButtonIntent,
          },
        '.filters-drawer__apply': {
          ...tertiaryButtonIntent,
        },
        '.footer__contact-btn, .footer__faq-btn': {
          ...tertiaryButtonIntent,
        },
        '.invoice--download__button, .basket__continue-shopping': {
          ...tertiaryButtonIntent,
        },
        '.shop-menu__sort': {
          ...tertiarySelectIntent,
        },
        '.recapOrder': {
          '&__product-name, &__product-state, &__date': {
            fontSize: '18px !important',
            '& h5': {
              [`@media (min-width: ${breakpoints.lg}px)`]: {
                fontSize: '18px !important',
              },
            },
          },
        },
      },
    },
    MuiButton: {
      root: {
        padding: '12px 32px',
        borderRadius: 4,
        fontSize: 16,
        fontWeight: 400,
        lineHeight: '24px',
        textTransform: 'none' as const,
      },
      sizeSmall: {
        padding: '8px 16px',
        fontSize: 14,
      },
      sizeLarge: {
        padding: '12px 32px',
      },
      // Bouton principal — 1 max par page
      // Homepage: "Découvrir le catalogue" | Produit: "Acheter"
      containedPrimary: {
        backgroundColor: ctaDefaut,
        color: white,
        boxShadow: 'none',
        '&:hover, &:focus, &:active, &.selected': {
          backgroundColor: ctaHover,
          boxShadow: 'none',
        },
      },
      contained: {
        backgroundColor: ctaDefaut,
        color: white,
        boxShadow: 'none',
        '&:hover, &:focus, &:active, &.selected': {
          backgroundColor: ctaHover,
          boxShadow: 'none',
        },
      },
      // Bouton secondaire — plusieurs par page
      // Homepage: tous sauf "Découvrir le catalogue" | Listing: "Bouton suivant"
      // Produit: "Réserver" | Footer: "Nous contacter", "FAQ"
      outlinedPrimary: {
        borderColor: ctaDefaut,
        color: ctaDefaut,
        backgroundColor: white,
        '&:hover, &:focus, &:active': {
          backgroundColor: ctaHover,
          borderColor: ctaHover,
          color: white,
        },
      },
      outlined: {
        padding: '12px 32px',
        borderWidth: 1,
        borderColor: ctaDefaut,
        color: ctaDefaut,
        backgroundColor: white,
        '&:hover, &:focus, &:active': {
          backgroundColor: ctaHover,
          borderColor: ctaHover,
          color: white,
        },
      },
      outlinedSizeLarge: {
        padding: '12px 32px',
      },
      outlinedSizeSmall: {
        padding: '8px 16px',
        fontSize: 14,
      },
      // Lien-bouton — Footer: liens Politique, Cookies, etc.
      textPrimary: {
        color: ctaDefaut,
        backgroundColor: 'transparent',
        padding: '4px 0',
        '&:hover, &:focus, &:active': {
          backgroundColor: 'transparent',
          color: ctaHover,
          textDecoration: 'none',
        },
      },
      text: {
        padding: '4px 0',
        color: ctaDefaut,
        '&:hover, &:focus, &:active': {
          backgroundColor: 'transparent',
          color: ctaHover,
        },
      },
    },
    // Bouton icône (carré) — variante filled: bg ctaDefaut | variante outlined: via className
    MuiIconButton: {
      root: {
        borderRadius: 4,
        padding: 12,
        color: white,
        backgroundColor: ctaDefaut,
        '&:hover': {
          backgroundColor: ctaHover,
        },
      },
      colorSecondary: {
        color: ctaDefaut,
        backgroundColor: white,
        border: `1px solid ${ctaDefaut}`,
        '&:hover': {
          backgroundColor: ctaDefaut,
          color: white,
        },
      },
    },
    MuiRadio: {
      root: {
        backgroundColor: 'transparent',
      },
    },
    // Lien texte — Footer: Politique, Cookies, etc.
    MuiLink: {
      root: {
        color: ctaDefaut,
        fontSize: 16,
        fontWeight: 400,
        lineHeight: '24px',
        textDecoration: 'none',
        '&:hover': {
          color: ctaHover,
          textDecoration: 'underline',
        },
      },
    },
    MuiCheckbox: {
      root: {
        border: 'none',
        '&:hover': {
          backgroundColor: white,
          color: black,
        },
      },
    },
    MuiPaper: {
      root: {
        boxShadow: '0px 0px 15px rgba(46, 46, 46, 0.1)',
      },
      rounded: {
        borderRadius: 4,
      },
    },
    MuiSelect: {
      root: {
        color: primaryMain,
      },
      selectMenu: {
        '&:focus': {
          background: white,
        },
      },
    },
    MuiChip: {
      root: {
        borderRadius: 0,
        fontSize: 15,
        fontWeight: 700,
        color: black,
        backgroundColor: 'transparent',
        height: 25,
        padding: 0,
      },
      label: {
        padding: 0,
      },
      sizeSmall: {
        height: 22,
        fontSize: 12,
        fontWeight: 400,
      },
      labelSmall: {
        paddingLeft: 10,
        paddingRight: 10,
      },
      colorPrimary: {
        color: primaryMain,
        backgroundColor: 'transparent',
      },
      colorSecondary: {
        color: secondaryMain,
        backgroundColor: 'transparent',
      },
    },
  },
})

export default theme
